// src/pages/settings/Backup.jsx
// Admin backup & restore: download ALL shop data as one file / restore from it.
import React, { useState, useRef } from 'react';
import api from '../../config/api';

const GREEN = '#2e9f86';

export default function Backup() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {kind:'ok'|'err'|'trial', text}
  const fileRef = useRef(null);

  // axios blob responses hide the JSON error body — read it back out
  const errInfo = async (err) => {
    let data = err.response?.data;
    if (data instanceof Blob) {
      try { data = JSON.parse(await data.text()); } catch { data = {}; }
    }
    return data || {};
  };

  const trialMsg = {
    kind: 'trial',
    text: '🔒 Backup & restore is available in the full version. Activate your account to protect your data — WhatsApp +961 70 385 816',
  };

  const download = async () => {
    setBusy(true); setMsg(null);
    try {
      const res = await api.get('/api/backup', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mmm-pos-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg({ kind: 'ok', text: 'Backup downloaded. Keep it somewhere safe (USB, Google Drive…).' });
    } catch (err) {
      const data = await errInfo(err);
      setMsg(data.reason === 'trial' ? trialMsg : { kind: 'err', text: data.error || 'Backup failed. Try again.' });
    } finally { setBusy(false); }
  };

  const restore = async (file) => {
    if (!file) return;
    if (!window.confirm(
      '⚠️ RESTORE will REPLACE ALL current data (products, documents, accounts, settings, users) ' +
      'with the data in this backup file.\n\nThis cannot be undone. Continue?'
    )) { fileRef.current.value = ''; return; }
    setBusy(true); setMsg(null);
    try {
      const text = await file.text();
      try { JSON.parse(text); } catch { throw new Error('This file is not a valid backup file.'); }
      // send the raw text — the server parses it with the same EJSON decoder that wrote it
      const res = await api.post('/api/backup/restore', { ejson: text });
      if (!res.data.documents) {
        setMsg({ kind: 'err', text: 'The backup file contained no records — nothing was restored. Check that the downloaded backup is from an account WITH data.' });
        return;
      }
      setMsg({ kind: 'ok', text: `✓ Restored ${res.data.documents} records in ${res.data.collections} collections (backup from ${res.data.backup_date ? new Date(res.data.backup_date).toLocaleDateString() : 'unknown date'}). Reloading…` });
      setTimeout(() => window.location.reload(), 2500);
    } catch (err) {
      const data = await errInfo(err);
      setMsg(data.reason === 'trial' ? trialMsg : { kind: 'err', text: data.error || err.message || 'Restore failed.' });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="p-5 max-w-2xl">
      {/* Backup card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-4">
        <h3 className="font-bold text-gray-800 mb-1">💾 Download Backup</h3>
        <p className="text-sm text-gray-500 mb-4">
          Saves <b>everything</b> — products, documents, accounts, payments, stock, settings and users —
          into one file. Do this regularly (at least weekly) and keep the file on a USB stick or cloud drive.
        </p>
        <button onClick={download} disabled={busy}
          className="px-5 py-2.5 rounded-lg text-white text-sm font-bold disabled:opacity-50"
          style={{ background: GREEN }}>
          {busy ? 'Working…' : '⬇ Download Backup'}
        </button>
      </div>

      {/* Restore card */}
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-5">
        <h3 className="font-bold text-gray-800 mb-1">♻️ Restore from Backup</h3>
        <p className="text-sm text-gray-500 mb-1">
          Upload a backup file to bring all data back — e.g. after a computer change or problem.
        </p>
        <p className="text-sm text-red-600 font-semibold mb-4">
          ⚠️ This replaces ALL current data with the backup. It cannot be undone.
        </p>
        <input ref={fileRef} type="file" accept=".json,application/json" disabled={busy}
          onChange={e => restore(e.target.files?.[0])}
          className="block text-sm text-gray-600 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-red-50 file:text-red-700 file:font-semibold file:cursor-pointer" />
      </div>

      {msg && (
        <div className={`mt-4 rounded-lg px-4 py-3 text-sm font-medium ${
          msg.kind === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : msg.kind === 'trial' ? 'bg-amber-50 text-amber-800 border border-amber-300'
          : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
}
